import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
  Box,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ShareIcon from "@mui/icons-material/Share";
import { Movie } from "../../utils/types";
import { logMovieCTA } from "../../service/analytics";

let shareListener: ((movie: Movie) => void) | null = null;

/**
 * Global function to trigger the share dialog from anywhere in the application.
 * @param movie The movie to share.
 */
export const showShare = (movie: Movie) => {
  console.log(
    "showShare called for movie:",
    movie?.title,
    "Listener registered:",
    !!shareListener,
  );
  if (shareListener) {
    shareListener(movie);
  }
};

export const ShareDialog = () => {
  const [open, setOpen] = useState(false);
  const [movie, setMovie] = useState<Movie | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    console.log("ShareDialog component mounted, registering shareListener");
    shareListener = (selectedMovie) => {
      console.log(
        "shareListener triggered, opening dialog for movie:",
        selectedMovie?.title,
      );
      setMovie(selectedMovie);
      setOpen(true);
    };
    return () => {
      console.log(
        "ShareDialog component unmounting, cleaning up shareListener",
      );
      shareListener = null;
    };
  }, []);

  // Reset fields when dialog opens/closes or movie changes
  useEffect(() => {
    if (open) {
      setRating(null);
      setComment("");
    }
  }, [open, movie]);

  const letterboxdLink = movie ? `https://letterboxd.com/tmdb/${movie.id}` : "";
  const promoText =
    "Check My Movies list (suggest-movies-live.firebaseapp.com) to check Latest/Upcoming movies and create your own watchlist/favorites.";

  const generateShareText = () => {
    if (!movie) return "";
    const parts = [`Check out "${movie.title}"`];
    if (rating !== null) {
      parts.push(`My rating: ${rating}/10`);
    }
    if (comment.trim()) {
      parts.push(`My comments: ${comment.trim()}`);
    }
    parts.push(`Check it here: ${letterboxdLink}`);
    parts.push(promoText);
    return parts.join("\n\n");
  };

  const handleShare = async () => {
    if (!movie) return;
    logMovieCTA("cta_click_share", movie, {
      share_platform: navigator.share ? "native_share" : "copy_clipboard",
      has_custom_rating: rating !== null,
      has_comment: !!comment.trim(),
    });
    const shareText = generateShareText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: movie.title,
          text: shareText,
        });
        onClose();
      } catch (err: any) {
        if (err.name !== "AbortError") {
          // If native share fails or is blocked, copy to clipboard
          try {
            await navigator.clipboard.writeText(shareText);
            onClose();
          } catch (clipErr) {
            console.error("Failed to copy to clipboard", clipErr);
          }
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        onClose();
      } catch (clipErr) {
        console.error("Failed to copy to clipboard", clipErr);
      }
    }
  };


  return (
    <Dialog
      open={open && !!movie}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 1,
        },
      }}
    >
      {movie && (
        <>
          <DialogTitle sx={{ fontWeight: "bold", textAlign: "center", pb: 1 }}>
            Share Movie
          </DialogTitle>

          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}
          >
            <Typography
              variant="subtitle1"
              align="center"
              sx={{ fontWeight: 500, color: "text.primary" }}
            >
              {movie.title}
            </Typography>

            {/* Rating Section */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 500, color: "text.secondary" }}
              >
                My rating (Optional)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Rating
                  name="movie-share-rating"
                  value={rating}
                  precision={0.5}
                  max={10}
                  size="medium"
                  onChange={(_, newValue) => setRating(newValue)}
                />
                {rating !== null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {rating}/10
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setRating(null)}
                      title="Clear rating"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Comment Section */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 500, color: "text.secondary" }}
              >
                My comments (Optional)
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Add a comment about the movie..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                variant="outlined"
                size="small"
                inputProps={{ maxLength: 500 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* Share Preview Section */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 500, color: "text.secondary" }}
              >
                Share Preview
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                  maxHeight: 120,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.8rem",
                  color: "text.secondary",
                }}
              >
                {generateShareText()}
              </Paper>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleShare}
              variant="contained"
              color="primary"
              startIcon={<ShareIcon />}
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {navigator.share ? "Share" : "Copy & Share"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
